/**
 * Browser adapter for opening a private document in a separate browsing
 * context after its URL has been resolved asynchronously.
 *
 * Web-only: Unity downloads a citation to disk and opens its in-app PDF view.
 * The web client receives a private S3 object as a local `blob:` URL instead.
 */

/** The outcome of reserving a destination tab up front. */
interface TabReservation {
  readonly tab: Window | null;
  /** Why no tab was reserved; `null` when one was. */
  readonly refusal: "no-activation" | "blocked" | null;
}

export interface IDocumentOpenService {
  /** Resolves and opens one document; false means unavailable or popup-blocked. */
  openAsync(resolveUrl: () => Promise<string | null>): Promise<boolean>;
}

function describeTabRefusal(reservation: TabReservation): string {
  if (reservation.refusal === null) return "The reserved tab could not be navigated.";
  if (reservation.refusal === "no-activation") {
    return (
      "No tab was reserved — this call carried no user activation (an agent tool " +
      "call rather than a tap), so the post-fetch open was the only attempt."
    );
  }
  return (
    "No tab was reserved — `window.open` was refused while the activation was " +
    "still live, so this browser is blocking the popup outright."
  );
}

export class BrowserDocumentOpenService implements IDocumentOpenService {
  async openAsync(resolveUrl: () => Promise<string | null>): Promise<boolean> {
    /*
     * WebKit does not preserve a tap's transient activation across the private
     * S3 read. Reserve the destination synchronously while the tap is live,
     * then navigate it once the bytes arrive. `Find_and_Show_PDF` has no live
     * tap, so it skips this reservation and retains the best-effort fallback.
     *
     * Open `about:blank` without window features so we retain a WindowProxy,
     * then sever `opener` before any document is loaded. The final URL is a
     * local `blob:` URL, so there is no network referrer to suppress.
     */
    const reservation = this.reserveDocumentTab();
    try {
      const url = await resolveUrl();
      if (!url) {
        // The two ways this call can fail are indistinguishable to the caller
        // — both surface one "could not open" notice — and this file used to
        // be silent, so telling them apart cost a triage cycle. Say which.
        console.warn(
          "[BrowserDocumentOpenService] the document URL did not resolve; " +
            "the storage read failed (see the preceding S3 warning). No tab was opened.",
        );
        this.closeDocumentTab(reservation.tab);
        return false;
      }
      if (this.navigateDocumentTab(reservation.tab, url)) return true;

      // Desktop browsers may still allow the post-fetch open. Mobile WebKit
      // normally will not, but returning false there lets the caller surface
      // its existing failure notice instead of claiming a blocked popup did.
      const opened = this.navigateDocumentTab(this.openBlankDocumentTab(), url);
      if (!opened) {
        console.warn(
          "[BrowserDocumentOpenService] the document resolved but the browser refused the tab. " +
            describeTabRefusal(reservation),
        );
      }
      return opened;
    } catch (error) {
      this.closeDocumentTab(reservation.tab);
      throw error;
    }
  }

  /**
   * Reserve a new tab only while this call still belongs to a real user action.
   *
   * ⚠ It reports WHY it did not, because the two reasons demand different
   * fixes and a bare `null` conflates them: "there was no tap" is the ordinary
   * state of an AI tool call and means the post-fetch open is the only attempt,
   * while "`window.open` was refused while the activation was still live" is a
   * popup blocker and means no attempt could ever have worked. The triage
   * warning below states one of these as fact, so it must not guess.
   */
  private reserveDocumentTab(): TabReservation {
    if (navigator.userActivation && !navigator.userActivation.isActive) {
      return { tab: null, refusal: "no-activation" };
    }
    const tab = this.openBlankDocumentTab();
    return { tab, refusal: tab ? null : "blocked" };
  }

  private openBlankDocumentTab(): Window | null {
    try {
      const tab = window.open("about:blank", "_blank");
      if (tab) tab.opener = null;
      return tab;
    } catch {
      return null;
    }
  }

  private navigateDocumentTab(tab: Window | null, url: string): boolean {
    if (!tab) return false;
    try {
      if (tab.closed) return false;
      tab.location.replace(url);
      return true;
    } catch {
      this.closeDocumentTab(tab);
      return false;
    }
  }

  private closeDocumentTab(tab: Window | null): void {
    if (!tab) return;
    try {
      if (!tab.closed) tab.close();
    } catch {
      // A browser-owned external context may stop exposing its WindowProxy.
    }
  }
}
